import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { LoadingColor, LoadingType } from '@constants/loading/1.0';
import { MessageType } from '@constants/message/1.0';
import { BrandService } from '@services/brand/1.0/brand.service';
import { PermissionsService } from '@services/permissions/1.0/permissions.service';
import { AuthService } from '@auth0/auth0-angular';
import { DecoratorService } from '@services/decorator/1.0/decorator.service';
import { catchError, map, filter, take } from 'rxjs/operators';
import { of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NgIf } from '@angular/common';
import { LoadingComponent } from '@components/loading/1.0/loading.component';
import { MessageComponent } from '@components/message/1.0/message.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'txp-role-routing',
  templateUrl: './role-routing.component.html',
  styleUrls: ['./role-routing.component.scss'],
  standalone: true,
  imports: [NgIf, LoadingComponent, MessageComponent, TranslateModule],
})
export class RoleRoutingComponent implements OnInit, OnDestroy {
  messageType = MessageType;
  isLoading = true;
  loadingColor = LoadingColor;
  loadingType = LoadingType;
  thirdPartyCookiesDisabled: boolean;
  noBrandsAssigned = false;
  private destroy$ = new Subject<void>();
  private hasRouted = false;

  constructor(
    private brandService: BrandService,
    private permissionsService: PermissionsService,
    private authService: AuthService,
    private router: Router,
    private decoratorService: DecoratorService,
  ) {
    const thirdPartyCookieSetting = /s=1/.test(document.cookie);
    if (!thirdPartyCookieSetting) {
      this.thirdPartyCookiesDisabled = true;
    }
  }

  ngOnInit() {
    this.permissionsService.loaded$
      .pipe(
        filter((isLoaded) => isLoaded),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        if (this.hasRouted) return;
        this.hasRouted = true;
        const tenantIds = this.permissionsService.tenantIds || [];
        if (tenantIds.length === 0) {
          this.isLoading = false;
          this.noBrandsAssigned = true;
          return;
        }
        this.routeUser();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private routeUser() {
    const tenantIds = [...new Set(this.permissionsService.tenantIds || [])];
    const brandId = window.sessionStorage.getItem('brand_id');

    const isSuperAdmin = tenantIds.includes('*');
    const isSingleTenantUser = tenantIds.length === 1 && !isSuperAdmin;
    const isMultiTenantUser = tenantIds.length > 1 && !isSuperAdmin;
    const hasPermissions = this.permissionsService.permissionsBackup.length > 0;

    const userEmail = this.permissionsService.userProfile?.email || '';
    const isInternalTillsterUser = userEmail.toLowerCase().endsWith('@tillster.com');

    // Internal Tillster users should always go to brand selection
    if (isInternalTillsterUser && isMultiTenantUser && !brandId) {
      this.isLoading = false;
      this.router.navigate(['brand'], { replaceUrl: true });
      return;
    }

    const handleResellerRouting = (resellerId: string) => {
      sessionStorage.setItem('reseller_id', resellerId);
      localStorage.setItem('active_tenant_identifier', resellerId);
      this.brandService.setResellerId(resellerId);

      this.decoratorService
        .get(resellerId, 'reseller-logo', true)
        .pipe(
          map((decorators) => {
            const logoDecorator = decorators.find((d: any) => d?.field === 'images');
            const logoUrl = logoDecorator?.value || null;
            if (logoUrl) {
              this.brandService.setResellerLogoUrl(logoUrl);
            }
            return logoUrl;
          }),
          catchError(() => {
            return this.decoratorService.get('GLOBAL', 'cross-tenant:reseller-logo', true).pipe(
              map((globalDecorators) => {
                const globalLogoDecorator = globalDecorators.find(
                  (d) => d.field === 'images' && d.key === resellerId,
                );
                const logoUrl = globalLogoDecorator?.value || null;
                if (logoUrl) {
                  this.brandService.setResellerLogoUrl(logoUrl);
                }
                return logoUrl;
              }),
              catchError(() => of(null)),
            );
          }),
        )
        .subscribe();

      this.isLoading = false;
      this.router.navigate(['brand'], { replaceUrl: true });
    };

    // Handle single-tenant user with permissions
    if (isSingleTenantUser && hasPermissions && !brandId) {
      const singleTenantId = tenantIds[0];

      const hasResellerProductOperationsRole = this.permissionsService.permissionsBackup.some(
        (permission) => permission.includes('ResellerProductOperations'),
      );

      if (hasResellerProductOperationsRole) {
        handleResellerRouting(singleTenantId);
        return;
      }

      sessionStorage.setItem('brand_id', singleTenantId);
      localStorage.setItem('active_tenant_identifier', singleTenantId);

      // Load full brand details before navigation so header shows brand name
      this.brandService.getAndSetCurrentBrand(singleTenantId);

      // Route based on permissions
      let targetRoute = 'brand';

      if (this.permissionsService.getRole('store:read')) {
        targetRoute = 'store';
      } else if (this.permissionsService.getRole('call-centers:read')) {
        targetRoute = 'call-center/app';
      }

      this.isLoading = false;
      this.router.navigate([targetRoute], { replaceUrl: true });
      return;
    }

    // Handle multi-tenant users without permissions (new backend behavior)
    if (isMultiTenantUser && !hasPermissions && !brandId) {
      this.authService
        .getAccessTokenSilently({
          authorizationParams: {
            active_tenant_identifier: tenantIds[0],
          },
          cacheMode: 'off',
        })
        .subscribe((token) => {
          this.permissionsService.setPermissionsFromJwt(token);

          const hasResellerProductOperationsRole = this.permissionsService.getRole(
            'ResellerProductOperations',
          );

          if (hasResellerProductOperationsRole) {
            handleResellerRouting(tenantIds[0]);
            return;
          }
          localStorage.setItem('active_tenant_identifier', tenantIds[0]);

          this.isLoading = false;
          this.router.navigate(['brand'], { replaceUrl: true });
        });
      return;
    }

    // Handle multi-tenant users with permissions (old backend behavior)
    if (isMultiTenantUser && hasPermissions && !brandId) {
      const hasResellerProductOperationsRole = this.permissionsService.permissionsBackup.some(
        (permission) => permission.includes('ResellerProductOperations'),
      );

      if (hasResellerProductOperationsRole) {
        handleResellerRouting(tenantIds[0]);
        return;
      }

      const storedTenantId = localStorage.getItem('active_tenant_identifier');

      if (storedTenantId && tenantIds.includes(storedTenantId)) {
        sessionStorage.setItem('brand_id', storedTenantId);
        this.brandService.getAndSetCurrentBrand(storedTenantId);
        this.isLoading = false;
        this.router.navigate(['store'], { replaceUrl: true });
        return;
      }

      sessionStorage.setItem('brand_id', tenantIds[0]);
      this.isLoading = false;
      this.router.navigate(['brand'], { replaceUrl: true });
      return;
    }

    const hasTenantSession = brandId && (tenantIds.includes(brandId) || tenantIds.includes('*'));

    if (hasTenantSession) {
      this.router.navigate(['brand', brandId, 'info'], { replaceUrl: true });
      this.isLoading = false;
      this.brandService.getAndSetCurrentBrand(brandId);
      return;
    }

    this.isLoading = false;
    this.router.navigate(['brand'], { replaceUrl: true });
  }
}
